import { ArrowRight, GripVertical, Package, Plus, Trash2 } from 'lucide-react'
import { formatCurrency } from '@/utils/format-currency'
import { tipoConfig } from './proposalStudioConstants'
import type { useProposalStudio } from './useProposalStudio'

type Studio = ReturnType<typeof useProposalStudio>

export function ProposalStudioItemsView({ s }: { s: Studio }) {
  return (
    <>
                {/* Left: Available Items */}
                <div className="w-[35%] border-r border-white/[0.06] flex flex-col">
                  <div className="p-4 border-b border-white/[0.06]">
                    <h3 className="text-xs font-bold text-accent uppercase tracking-wider mb-2">
                      Planos e Serviços
                    </h3>
                    <p className="text-[10px] text-text-label">
                      Clique para adicionar à proposta
                    </p>
                  </div>
                  <div className="flex-1 overflow-y-auto p-4 space-y-2">
                    {s.loading ? (
                      <p className="text-sm text-text-muted">Carregando...</p>
                    ) : s.availableItems.length === 0 ? (
                      <p className="text-sm text-text-muted">Nenhum plano disponível</p>
                    ) : (
                      s.availableItems.map((avail) => {
                        const cfg = tipoConfig[avail.tipo] || tipoConfig.plano
                        const Icon = cfg.icon
                        return (
                          <button
                            key={avail.id}
                            onClick={() => s.handleAddItem(avail)}
                            className="w-full flex items-center gap-3 p-3 rounded-lg bg-white/[0.03] border border-white/[0.06] hover:border-accent/30 hover:bg-white/[0.05] transition-all text-left group"
                          >
                            <div
                              className="w-8 h-8 rounded-md flex items-center justify-center shrink-0"
                              style={{ backgroundColor: `${cfg.color}20` }}
                            >
                              <Icon size={14} style={{ color: cfg.color }} />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm text-text-primary truncate">{avail.nome}</p>
                              {avail.descricao && (
                                <p className="text-[10px] text-text-label truncate">{avail.descricao}</p>
                              )}
                            </div>
                            <div className="text-right shrink-0">
                              <p className="text-sm font-medium text-accent">{formatCurrency(avail.valor)}</p>
                              <p className="text-[10px] text-text-label">
                                {avail.recorrente ? 'Recorrente' : 'Avulso'}
                              </p>
                            </div>
                            <ArrowRight
                              size={14}
                              className="text-text-muted opacity-0 group-hover:opacity-100 transition-opacity shrink-0"
                            />
                          </button>
                        )
                      })
                    )}
                    <button
                      onClick={s.handleAddCustomItem}
                      className="w-full flex items-center justify-center gap-2 p-3 rounded-lg border border-dashed border-white/[0.1] hover:border-accent/30 hover:bg-white/[0.03] transition-all text-text-muted hover:text-accent"
                    >
                      <Plus size={14} />
                      <span className="text-sm">Adicionar Item Customizado</span>
                    </button>
                  </div>
                </div>

                {/* Right: Proposal Builder */}
                <div className="w-[65%] flex flex-col">
                  {/* Totals bar */}
                  <div className="p-4 border-b border-white/[0.06] bg-white/[0.02]">
                    <div className="flex items-center gap-6">
                      <div>
                        <p className="text-[10px] text-text-label uppercase tracking-wider">Valor Total</p>
                        <p className="text-xl font-bold text-text-primary">{formatCurrency(s.totals.valorTotal)}</p>
                      </div>
                      {s.totals.valorMensal > 0 && (
                        <div>
                          <p className="text-[10px] text-text-label uppercase tracking-wider">Mensal</p>
                          <p className="text-lg font-semibold text-emerald-400">{formatCurrency(s.totals.valorMensal)}</p>
                        </div>
                      )}
                      {s.totals.valorSetup > 0 && (
                        <div>
                          <p className="text-[10px] text-text-label uppercase tracking-wider">Setup</p>
                          <p className="text-lg font-semibold text-amber-400">{formatCurrency(s.totals.valorSetup)}</p>
                        </div>
                      )}
                      <div className="flex-1" />
                      <div className="text-right">
                        <p className="text-[10px] text-text-label uppercase tracking-wider">Itens</p>
                        <p className="text-lg font-semibold text-text-primary">{s.proposalItems.length}</p>
                      </div>
                    </div>
                  </div>

                  {/* Items list */}
                  <div className="flex-1 overflow-y-auto p-4">
                    {s.proposalItems.length === 0 ? (
                      <div className="h-full flex flex-col items-center justify-center text-text-muted">
                        <Package size={32} className="mb-3 opacity-30" />
                        <p className="text-sm">Nenhum item na proposta</p>
                        <p className="text-xs mt-1">Clique nos planos à esquerda para adicionar</p>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {s.proposalItems.map((item, index) => {
                          const cfg = tipoConfig[item.tipo] || tipoConfig.plano
                          const Icon = cfg.icon
                          const isRecurring = item.tipo === 'plano' || item.tipo === 'servico'
                          const isDragOver = s.dragOverIndex === index
                          const isDragged = s.draggedIndex === index

                          return (
                            <div
                              key={item.id}
                              draggable
                              onDragStart={() => s.handleDragStartItem(index)}
                              onDragOver={(e) => s.handleDragOverItem(e, index)}
                              onDrop={(e) => s.handleDropItem(e, index)}
                              onDragEnd={s.handleDragEndItem}
                              className={`
                                flex items-center gap-3 p-3 rounded-lg border transition-all
                                ${isDragged ? 'opacity-50' : 'opacity-100'}
                                ${isDragOver ? 'border-accent/50 bg-accent/5' : 'border-white/[0.06] bg-white/[0.03]'}
                              `}
                            >
                              <div className="cursor-grab active:cursor-grabbing text-text-muted hover:text-text-primary">
                                <GripVertical size={16} />
                              </div>

                              <div
                                className="w-8 h-8 rounded-md flex items-center justify-center shrink-0"
                                style={{ backgroundColor: `${cfg.color}20` }}
                              >
                                <Icon size={14} style={{ color: cfg.color }} />
                              </div>

                              <div className="flex-1 min-w-0 space-y-1.5">
                                <input
                                  value={item.descricao}
                                  onChange={(e) => s.handleUpdateDescricao(item.id, e.target.value)}
                                  className="w-full bg-transparent text-sm text-text-primary font-medium outline-none border-b border-transparent focus:border-accent/30 pb-0.5"
                                />
                                <div className="flex items-center gap-2">
                                  <span
                                    className="text-[10px] px-1.5 py-0.5 rounded font-medium"
                                    style={{ backgroundColor: `${cfg.color}20`, color: cfg.color }}
                                  >
                                    {cfg.label}
                                  </span>
                                  <span className="text-[10px] text-text-label">
                                    {isRecurring ? 'Recorrente' : 'Avulso'}
                                  </span>
                                </div>
                              </div>

                              <div className="flex items-center gap-2 shrink-0">
                                <div className="flex items-center gap-1">
                                  <button
                                    onClick={() => s.handleUpdateQty(item.id, item.quantidade - 1)}
                                    className="w-6 h-6 rounded bg-white/[0.06] text-text-muted hover:text-text-primary flex items-center justify-center text-xs"
                                  >
                                    -
                                  </button>
                                  <span className="text-sm text-text-primary w-6 text-center">
                                    {item.quantidade}
                                  </span>
                                  <button
                                    onClick={() => s.handleUpdateQty(item.id, item.quantidade + 1)}
                                    className="w-6 h-6 rounded bg-white/[0.06] text-text-muted hover:text-text-primary flex items-center justify-center text-xs"
                                  >
                                    +
                                  </button>
                                </div>

                                <input
                                  type="number"
                                  value={item.valorUnitario}
                                  onChange={(e) => s.handleUpdateValor(item.id, Number(e.target.value))}
                                  className="w-20 bg-white/[0.04] border border-white/[0.06] text-text-primary text-sm rounded-md px-2 py-1 outline-none focus:border-accent text-right"
                                />

                                <div className="w-24 text-right">
                                  <p className="text-sm font-medium text-text-primary">
                                    {formatCurrency(item.total)}
                                  </p>
                                </div>

                                <button
                                  onClick={() => s.handleRemoveItem(item.id)}
                                  className="p-1.5 rounded-md text-text-muted hover:text-red-400 hover:bg-red-400/10 transition-colors"
                                >
                                  <Trash2 size={14} />
                                </button>
                              </div>
                            </div>
                          )
                        })}
                      </div>
                    )}
                  </div>
                </div>
    </>
  )
}
